import { useLayoutEffect, useRef } from 'react'

export const FLIP_MOVE_MS = 600
export const FLIP_ENTER_MS = 500
const MAX_ANIMATED_CHANGES = 3

const prefersReducedMotion = () =>
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

// Smoothly slides remaining cards into place (FLIP) when a few cards leave or join the list.
// Bulk changes (pagination, sorting, view switch) are not animated.
export default function useFlipList(containerRef, ids, layoutKey) {
    const positions = useRef(new Map())
    const previousIds = useRef([])
    const previousLayoutKey = useRef(layoutKey)

    useLayoutEffect(() => {
        const container = containerRef.current
        const children = container ? Array.from(container.children) : []
        const next = new Map()
        if (container && children.length === ids.length) {
            const base = container.getBoundingClientRect()
            children.forEach((element, index) => {
                const rect = element.getBoundingClientRect()
                next.set(String(ids[index]), {
                    element,
                    x: rect.left - base.left,
                    y: rect.top - base.top,
                })
            })
        }

        const previous = positions.current
        const changed =
            previousIds.current.length !== ids.length ||
            previousIds.current.some((id, index) => String(id) !== String(ids[index]))
        const added = [...next.keys()].filter((id) => !previous.has(id))
        const removed = [...previous.keys()].filter((id) => !next.has(id))

        if (
            changed &&
            previous.size > 0 &&
            previousLayoutKey.current === layoutKey &&
            added.length + removed.length <= MAX_ANIMATED_CHANGES &&
            !prefersReducedMotion()
        ) {
            next.forEach(({ element, x, y }, id) => {
                if (typeof element.animate !== 'function') return
                const old = previous.get(id)
                if (!old) {
                    element.animate(
                        [
                            { opacity: 0, transform: 'scale(0.96)' },
                            { opacity: 1, transform: 'none' },
                        ],
                        { duration: FLIP_ENTER_MS, delay: 150, easing: 'ease-out', fill: 'both' },
                    )
                    return
                }
                const dx = old.x - x
                const dy = old.y - y
                if (!dx && !dy) return
                element.animate(
                    [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }],
                    { duration: FLIP_MOVE_MS, easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)' },
                )
            })
        }

        positions.current = next
        previousIds.current = ids
        previousLayoutKey.current = layoutKey
    })
}
